import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileCheck, FileText, Shield } from 'lucide-react';
import { ClientPicker } from '../components/ClientPicker';
import { TemplatePickerModal } from '../components/TemplatePickerModal';
import { TemplateEditorModal } from '../components/TemplateEditorModal';
import { FileUploadSection } from '../components/documents/FileUploadSection';
import { fillTemplate } from '../utils/templates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewDocument() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Template state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateContent, setTemplateContent] = useState('');
  const [businessProfile, setBusinessProfile] = useState(null);
  
  // File upload state
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSource, setFileSource] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  useEffect(() => {
    // If there's a client ID from URL parameter, fetch the client name
    const fetchClientName = async () => {
      const preSelectedClientId = searchParams.get('client');
      if (preSelectedClientId && !clientName) {
        try {
          const response = await axios.get(`${API}/clients/${preSelectedClientId}`, { 
            withCredentials: true 
          });
          if (response.data) {
            setClientName(response.data.name);
          }
        } catch (error) {
          console.error('Failed to fetch client name:', error);
        }
      }
    };
    
    fetchClientName();
  }, [searchParams, clientName]);

  const fetchBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setBusinessProfile(response.data.business_profile || {});
    } catch (error) {
      console.error('Failed to fetch business profile:', error);
    }
  };

  const templates = [
    { label: 'Service Agreement', type: 'contract', icon: FileCheck },
    { label: 'Model Release', type: 'model_release', icon: FileText },
    { label: 'Liability Waiver', type: 'permit', icon: Shield },
  ];

  const handleTemplate = (type, label) => {
    setDocType(type);
    setTitle(label);
  };

  const handleSelectTemplate = (templateId) => {
    if (!clientName) {
      alert('Please select a client first');
      setShowTemplatePicker(false);
      return;
    }
    
    const content = fillTemplate(templateId, businessProfile || {}, clientName);
    setTemplateContent(content);
    setShowTemplatePicker(false);
    setShowTemplateEditor(true);
  };

  const handleSaveTemplate = (content) => {
    setTemplateContent(content);
    setFileSource('template');
    setShowTemplateEditor(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFileUrl(response.data.file_url);
      setFileName(response.data.file_name);
      setFileSource(response.data.file_source);
      setFileSize(response.data.file_size);
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = (url) => {
    if (!url.startsWith('https://')) {
      setUploadError('URL must start with https://');
      return;
    }

    try {
      const urlObj = new URL(url);
      setFileUrl(url);
      setFileName(urlObj.hostname);
      setFileSource('url');
      setFileSize('');
      setUrlInput('');
      setUploadError('');
    } catch {
      setUploadError('Invalid URL');
    }
  };

  const handleRemoveFile = () => {
    setFileUrl('');
    setFileName('');
    setFileSource('');
    setFileSize('');
    setUrlInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!clientId) {
      setError('Please select a client');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API}/documents`,
        {
          client_id: clientId,
          title,
          doc_type: docType,
          status: 'draft',
          expiry_date: expiryDate || undefined,
          content: templateContent || undefined,
          file_url: fileUrl || undefined,
          file_name: fileName || undefined,
          file_source: fileSource || undefined,
          file_size: fileSize || undefined
        },
        { withCredentials: true }
      );
      navigate('/documents');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 pt-4 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/documents')}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-text-muted" />
          </button>
          <h1 className="text-2xl font-semibold text-foreground">New Document</h1>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Client</label>
            <ClientPicker
              value={clientId}
              onChange={(id, name) => {
                setClientId(id);
                setClientName(name);
              }}
              error={error && !clientId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Quick Templates
            </label>
            <div className="grid grid-cols-3 gap-3">
              {templates.map((temp) => (
                <button
                  key={temp.type}
                  type="button"
                  onClick={() => handleTemplate(temp.type, temp.label)}
                  className={`p-4 rounded-lg border transition-colors text-center ${
                    docType === temp.type
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary'
                  }`}
                >
                  <temp.icon className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <span className="text-xs text-foreground">{temp.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground"
              placeholder="e.g., Wedding Photography Contract"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Document Type
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground"
              required
            >
              <option value="">Select type</option>
              <option value="contract">Contract</option>
              <option value="model_release">Model Release</option>
              <option value="permit">Permit/Waiver</option>
              <option value="invoice">Invoice</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Expiry Date (Optional)
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground"
            />
          </div>

          {/* Use Template Button */}
          <div>
            <button
              type="button"
              onClick={() => setShowTemplatePicker(true)}
              className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-primary hover:bg-surface-hover transition-colors font-medium"
            >
              📄 Use Template
            </button>
            {templateContent && (
              <p className="text-xs text-primary mt-2">✓ Template content added</p>
            )}
          </div>

          {/* Attach File Section */}
          <FileUploadSection
            fileUrl={fileUrl}
            fileName={fileName}
            fileSize={fileSize}
            uploading={uploading}
            uploadError={uploadError}
            onFileUpload={handleFileUpload}
            onUrlSubmit={handleUrlSubmit}
            onRemove={handleRemoveFile}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 font-medium"
          >
            {loading ? 'Creating...' : 'Create Document'}
          </button>
        </form>
      </div>

      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      <TemplateEditorModal
        isOpen={showTemplateEditor}
        onClose={() => setShowTemplateEditor(false)}
        templateContent={templateContent}
        onSave={handleSaveTemplate}
      />
    </div>
  );
}
